// Menu.js — a navegação do site no CELULAR, refeita do zero em 18/09/2026.
//
// ================= O QUE ELE ERA =================
//
// Uma coluna de seis botões iguais, cada um com gradiente e sombra colorida,
// dentro de uma gaveta com outro gradiente. Não tinha conta, não tinha busca,
// não tinha o carrinho com a contagem, não marcava a página em que a pessoa
// estava, e o "Carrinho" azul com o "Perfil" laranja competiam com o resto por
// atenção sem nenhum dos dois ser o principal. O dono resumiu: "é feio e bem
// desatualizado para o modelo de coisas que temos hoje em dia".
//
// ================= O QUE ELE É AGORA =================
//
// Uma gaveta em quatro blocos, na ordem em que a pergunta aparece:
//
//   1. QUEM SOU EU — foto, nome e OS DOIS SALDOS (Tickets e Insigne Coins),
//      com o caminho para a conta; sem conta, os dois botões de entrada. Era o
//      que mais faltava: no celular, o cabeçalho não tem espaço para o bloco de
//      perfil nem para os saldos, então o menu era o único lugar onde isso
//      caberia — e ele não tinha. Os saldos entraram em 18/09/2026, a pedido do
//      dono ("falta as moedas e insignas no navbar principal").
//   2. BUSCA — o campo some do cabeçalho em telas estreitas (ver o comentário
//      de origem em Header.js), e o catálogo fica a dois toques. Aqui ele custa
//      uma linha e leva direto para /livros?busca=.
//   3. ONDE IR — as rotas do site, com a atual MARCADA. Sem o marcador, a
//      gaveta é uma lista de links igual em toda página.
//   4. O QUE É MEU — carrinho (com a contagem), lista, e-books e créditos.
//      Sem conta, cada um leva ao login levando junto o destino, para a pessoa
//      voltar exatamente onde tentou entrar (o mesmo contrato de
//      destinoAposEntrar.js).
//
// E no pé: tema, "publique seu livro", redes e o telefone.
//
// ================= REGRAS QUE ELE CARREGA =================
//
//  - FECHA SOZINHO ao trocar de rota. A gaveta aberta por cima da página nova é
//    o defeito clássico deste componente em toda SPA.
//  - Esc fecha, clique no véu fecha, e o corpo para de rolar enquanto está
//    aberta (sem isso, rolar "dentro" rola a página atrás).
//  - O FOCO VAI PARA O BOTÃO DE FECHAR ao abrir: quem navega por teclado ou
//    leitor de tela entra na gaveta, e não continua na página atrás dela.
//  - A altura é `100dvh`, e não `100vh`: no celular a barra do navegador entra
//    e sai, e com `vh` o pé da gaveta fica embaixo dela.
//  - ELE NÃO MORA DENTRO DO CABEÇALHO, e isso é um PORTAL de propósito. O
//    <header> é `fixed` com `z-[101]` e carrega um `transform` — cada uma
//    dessas três coisas cria uma CAMADA PRÓPRIA, e dentro dela o `z-9999` da
//    gaveta vale só até 101. O resultado, que o dono fotografou: a barra de
//    compra do celular (z-9998, na página) aparecia POR CIMA do menu aberto.
//    Mandando a gaveta para o `document.body`, ela volta a disputar camada
//    com o resto da página e ganha.
import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SITES, SITE_ATUAL } from "../../config/sites";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Menu.css";
import logo from "../../assets/imgs/logo.png";
import { useAuth } from "../../services/context/AuthContext";
import { CarrinhoContext } from "../../services/context/CarrinhoContext";
import useProfileData from "../../hooks/userProfileData";
import FotoPessoa from "../ui/FotoPessoa";
import SaldoCreditos from "../perfil/creditos/SaldoCreditos";
import { getTheme, toggleTheme } from "../../theme";
import {
  TELEFONE_TEXTO,
  TELEFONE_HREF,
  WHATSAPP_PUBLICAR_HREF,
} from "../../config/contato";

const main = process.env.REACT_APP_MAIN;

/** As rotas do site, na ordem do cabeçalho do desktop. */
const ROTAS = [
  { to: main, label: "Início", icone: "bi-house-door", exato: true },
  { to: main + "livros", label: "Livros", icone: "bi-book-half" },
  { to: main + "autores", label: "Autores", icone: "bi-people" },
  { to: main + "clube", label: "Clube Insigne", icone: "bi-stars" },
  { to: main + "faq", label: "Dúvidas frequentes", icone: "bi-patch-question" },
  { to: main + "contato", label: "Contato", icone: "bi-headset" },
];

/**
 * O que é da conta. `aba` é a aba do perfil que o item abre — e, para quem não
 * entrou, é o que viaja junto para o login (ver destinoAposEntrar.js).
 */
const DA_CONTA = [
  { key: "meus-livros", label: "Meus e-books", icone: "bi-collection-play" },
  { key: "minha-lista", label: "Minha lista", icone: "bi-heart" },
  { key: "creditos", label: "Créditos", icone: "bi-coin" },
];

const Menu = ({ toggleSidebar, isOpen, setIsOpen }) => {
  const { user } = useAuth();
  const { profileData } = useProfileData();
  const navigate = useNavigate();
  const location = useLocation();
  const painelRef = useRef(null);
  const fecharRef = useRef(null);
  const [busca, setBusca] = useState("");
  const [tema, setTema] = useState(getTheme());

  const fechar = () => setIsOpen(false);

  // Trocar de rota fecha a gaveta. `location.key` muda até quando se navega
  // para a MESMA rota, que é o caso de clicar em "Livros" estando em /livros.
  useEffect(() => {
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const aoTeclar = (e) => e.key === "Escape" && fechar();
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", aoTeclar);
    // O foco entra na gaveta. `requestAnimationFrame` porque o botão só existe
    // depois de a folha ser pintada.
    const quadro = requestAnimationFrame(() => fecharRef.current?.focus());
    return () => {
      document.body.style.overflow = antes;
      document.removeEventListener("keydown", aoTeclar);
      cancelAnimationFrame(quadro);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const rotaAtual = (r) =>
    r.exato ? location.pathname === r.to : location.pathname.startsWith(r.to);

  const irParaBusca = (e) => {
    e.preventDefault();
    const termo = busca.trim();
    navigate(main + "livros" + (termo ? "?busca=" + encodeURIComponent(termo) : ""));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** Item da conta: com login abre a aba; sem login leva o destino junto. */
  const irParaConta = (item) => {
    if (user) {
      navigate(main + "perfil");
      // Os 150ms são os mesmos do cabeçalho: dão tempo de o Perfil montar e
      // registrar o ouvinte antes de o evento ser disparado.
      setTimeout(
        () => window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: item.key })),
        150
      );
    } else {
      navigate(main + "login", { state: { de: main + "perfil", aba: item.key } });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const linha =
    "foco-laranja flex w-full items-center gap-3 rounded-card border-2 px-3.5 py-3 text-left " +
    "text-[15px] font-semibold transition-colors";
  const linhaParada =
    "border-insigne-sombra bg-insigne-navy-soft text-white/85 hover:border-insigne-orange/50 hover:text-white";

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex justify-end" role="dialog" aria-modal="true" aria-label="Menu">
      <div className="menu-veu absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={fechar} />

      <aside
        ref={painelRef}
        className="menu-painel relative flex h-[100dvh] w-[min(92vw,380px)] flex-col overflow-y-auto
                   border-l-2 border-insigne-sombra bg-insigne-navy-deep"
      >
        {/* ===== Topo ===== */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b-2 border-insigne-sombra bg-insigne-navy-deep px-4 py-3">
          <Link to={main} onClick={fechar} className="foco-laranja rounded-controle">
            <img src={logo} alt="Insigne Acadêmica" className="h-8 w-auto" />
          </Link>
          <button
            ref={fecharRef}
            type="button"
            onClick={fechar}
            aria-label="Fechar menu"
            className="foco-laranja inline-flex h-10 w-10 items-center justify-center rounded-controle
                       border-2 border-insigne-sombra text-white/80 transition-colors hover:text-insigne-orange"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-5 px-4 py-4">
          {/* ===== 1. Quem sou eu ===== */}
          {user ? (
            // O cartão é UM bloco com DUAS peças: o link do perfil em cima e os
            // saldos embaixo. Os saldos ficam FORA do <Link> de propósito — o
            // "+" deles é um botão, e botão dentro de link é HTML inválido (e,
            // na prática, um toque que dispara as duas coisas).
            <div className="rounded-card border-2 border-insigne-sombra bg-insigne-navy-soft shadow-[3px_3px_0_0_rgb(var(--c-sombra))]">
              <Link
                to={main + "perfil"}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="foco-laranja flex items-center gap-3 rounded-card px-3.5 py-3 transition-colors hover:text-insigne-orange"
              >
                <FotoPessoa pessoa={profileData} nome={profileData?.nome} tamanho="w-11 h-11 text-sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold text-white">
                    {(profileData?.nome || "Minha conta").split(" ")[0]}
                  </span>
                  <span className="block text-xs text-white/55">Ver meu perfil</span>
                </span>
                <i className="bi bi-chevron-right shrink-0 text-white/40" aria-hidden="true" />
              </Link>

              {/* OS DOIS SALDOS, o mesmo componente do cabeçalho do computador
                  e do perfil — e não uma terceira leitura do saldo. Ele guarda
                  a resposta num cache de módulo por 60s, então abrir o menu não
                  repete a consulta que o cabeçalho já fez (o banco desta
                  hospedagem tem teto de conexões por hora). O "+" leva para a
                  Loja de Créditos, como no cabeçalho. */}
              <div className="border-t-2 border-insigne-sombra px-3.5 py-2.5">
                <SaldoCreditos
                  layout="linha"
                  className="justify-between"
                  onComprar={() => irParaConta({ key: "creditos" })}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              <Link
                to={main + "login"}
                className="btn-solido foco-laranja flex items-center justify-center gap-2 rounded-controle py-2.5 text-sm font-bold"
              >
                <i className="bi bi-box-arrow-in-right" aria-hidden="true" /> Entrar
              </Link>
              <Link
                to={main + "criar-conta"}
                className="foco-laranja flex items-center justify-center gap-2 rounded-controle border-2
                           border-insigne-orange py-2.5 text-sm font-bold text-insigne-orange
                           transition-colors hover:bg-insigne-orange hover:text-paper"
              >
                Criar conta
              </Link>
            </div>
          )}

          {/* ===== 2. Busca ===== */}
          <form onSubmit={irParaBusca} className="relative">
            <i className="bi bi-search pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-white/40" aria-hidden="true" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              type="search"
              placeholder="Buscar livro ou autor"
              aria-label="Buscar no catálogo"
              className="foco-laranja w-full rounded-controle border-2 border-insigne-sombra bg-insigne-navy-soft
                         py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-white/40 outline-none"
            />
          </form>

          {/* ===== 3. Onde ir ===== */}
          <nav className="flex flex-col gap-2" aria-label="Páginas do site">
            {ROTAS.map((r) => {
              const ativa = rotaAtual(r);
              return (
                <Link
                  key={r.to}
                  to={r.to}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  aria-current={ativa ? "page" : undefined}
                  className={
                    linha + " " +
                    (ativa
                      ? "border-insigne-orange bg-insigne-orange/15 text-white"
                      : linhaParada)
                  }
                >
                  <i className={"bi " + r.icone + " text-lg " + (ativa ? "text-insigne-orange" : "text-white/50")} aria-hidden="true" />
                  <span className="flex-1">{r.label}</span>
                  {ativa && <span className="h-2 w-2 shrink-0 rounded-full bg-insigne-orange" aria-hidden="true" />}
                </Link>
              );
            })}
          </nav>

          {/* ===== Os outros sites da Insigne ===== */}
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/35">
              Sites da Insigne
            </p>
            <div className="flex flex-col gap-2">
              {SITES.map((s) => {
                const aqui = s.chave === SITE_ATUAL;
                const miolo = (
                  <>
                    <i className={"bi " + s.icone + " text-lg " + (aqui ? "text-insigne-orange" : "text-white/50")} aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block">{s.rotulo}</span>
                      <span className="block text-xs font-normal text-white/50">{aqui ? "Você está aqui" : s.resumo}</span>
                    </span>
                    {!aqui && <i className="bi bi-box-arrow-up-right text-xs text-white/30" aria-hidden="true" />}
                  </>
                );
                return aqui ? (
                  <div key={s.chave} aria-current="page" className={linha + " border-insigne-orange/60 bg-insigne-orange/10 text-white"}>
                    {miolo}
                  </div>
                ) : (
                  <a key={s.chave} href={s.url} className={linha + " " + linhaParada}>
                    {miolo}
                  </a>
                );
              })}
            </div>
          </div>

          {/* ===== 4. O que é meu ===== */}
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/35">
              Minha conta
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to={main + "carrinho"}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className={linha + " " + linhaParada}
              >
                <i className="bi bi-cart text-lg text-white/50" aria-hidden="true" />
                <span className="flex-1">Carrinho</span>
                {/* A contagem mora no contexto do carrinho, a mesma do
                    cabeçalho — o menu não conta itens por conta própria. */}
                <ContadorDoCarrinho />
              </Link>

              {DA_CONTA.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => irParaConta(item)}
                  className={linha + " " + linhaParada}
                >
                  <i className={"bi " + item.icone + " text-lg text-white/50"} aria-hidden="true" />
                  <span className="flex-1">{item.label}</span>
                  <i className="bi bi-chevron-right text-xs text-white/30" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          {/* ===== Tema ===== */}
          <button
            type="button"
            onClick={() => setTema(toggleTheme())}
            className={linha + " " + linhaParada}
          >
            <i
              className={"bi text-lg text-white/50 " + (tema === "light" ? "bi-moon-stars-fill" : "bi-brightness-high-fill")}
              aria-hidden="true"
            />
            <span className="flex-1">{tema === "light" ? "Tema claro" : "Tema escuro"}</span>
            <span className="text-xs font-bold text-insigne-orange">trocar</span>
          </button>

          {/* ===== Publique seu livro ===== */}
          <a
            href={WHATSAPP_PUBLICAR_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="foco-laranja flex items-center gap-3 rounded-card border-2 border-insigne-orange/50
                       bg-insigne-orange/[0.08] px-3.5 py-3 transition-colors hover:bg-insigne-orange/15"
          >
            <i className="bi bi-tag-fill text-lg text-insigne-orange" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-bold text-white">Publique seu livro</span>
              <span className="block text-xs text-white/55">Fale com a editora no WhatsApp</span>
            </span>
          </a>
        </div>

        {/* ===== Pé ===== */}
        <div className="mt-auto border-t-2 border-insigne-sombra px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <div className="flex justify-center gap-3">
            {[
              { href: "https://www.linkedin.com/company/insigneacad%C3%AAmica/", icone: "bi-linkedin", nome: "LinkedIn" },
              { href: "https://www.tiktok.com/@insigneacademica", icone: "bi-tiktok", nome: "TikTok" },
              { href: "https://www.instagram.com/insigneacademica", icone: "bi-instagram", nome: "Instagram" },
            ].map((s) => (
              <a
                key={s.icone}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.nome}
                className="foco-laranja inline-flex h-11 w-11 items-center justify-center rounded-controle
                           border-2 border-insigne-sombra text-white/70 transition-colors
                           hover:border-insigne-orange hover:text-insigne-orange"
              >
                <i className={"bi " + s.icone} aria-hidden="true" />
              </a>
            ))}
          </div>

          <a
            href={TELEFONE_HREF}
            className="foco-laranja mt-3 flex items-center justify-center gap-2 rounded-controle text-sm text-white/55 transition-colors hover:text-insigne-orange"
          >
            <i className="bi bi-telephone" aria-hidden="true" /> {TELEFONE_TEXTO}
          </a>
        </div>
      </aside>
    </div>,
    document.body
  );
};

/**
 * A bolinha com o número de itens do carrinho.
 *
 * Componente à parte para o menu inteiro não renderizar de novo a cada item
 * somado ao carrinho: quem assina o contexto é só esta bolinha.
 */
const ContadorDoCarrinho = () => {
  const { totalItens } = useContext(CarrinhoContext);
  if (!totalItens) return null;
  return (
    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-selo bg-insigne-orange px-1.5 text-xs font-extrabold text-paper">
      {totalItens}
    </span>
  );
};

export default Menu;
